import isEqual from 'lodash/isEqual';
import joi from 'joi';
import {
  $criteria,
  ActionTypes,
  ImpactMode,
  ObtainRewardAction,
  PeriodicityLimitationEnum,
  RuleAttributes,
  RuleI18nAttributes,
  RuleOptions,
  RuleType,
  TransactionAction,
  TransactionFixedImpact,
  periodicityLimitationSchema,
  ruleI18nAttributesSchema,
} from '@loyalty/shared-common/rule-api-client';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { EventName, EventSource, MakeOptional } from '@loyalty/shared-common';
import { RewardAttributes } from '@loyalty/shared-common/rewards-api-client';

export interface BurningRuleActionTransImpact {
  value: number;
  currency: string;
}

export interface BurningRuleActionRewardAttributionImpact {
  rewardId: string;
}

export interface BurningRuleFormValue {
  // TODO move to BurningRuleForm
  key?: string;
  name: string;
  i18nAttributes: RuleI18nAttributes;
  criteria: Record<string, any> | undefined | null;
  options: RuleOptions;
  actions: {
    reward: BurningRuleActionRewardAttributionImpact;
    trans: MakeOptional<BurningRuleActionTransImpact, 'value'>;
  };
  entity?: Partial<RuleAttributes>;
}

export const burningRulesForm = joi.object<BurningRuleFormValue>({
  name: joi.string().required(),
  criteria: $criteria,
  i18nAttributes: ruleI18nAttributesSchema,
  options: joi.object({ periodicityLimitation: periodicityLimitationSchema }).required(),
  actions: joi
    .object({
      reward: joi
        .object({
          rewardId: joi.string().required(),
        })
        .required(),
      trans: joi
        .object({
          value: joi
            .number()
            .min(0)
            .max(99999999999)
            .custom(value => parseInt(value, 10))
            .required(),
          currency: joi.string().uuid().required(),
        })
        .required(),
    })
    .required(),
});

export function attributesToFormValue(
  rule: Partial<RuleAttributes> | undefined,
  currencies: CurrencyAttributes[],
  rewards: RewardAttributes[],
  languages: string[],
  timezone: string,
): BurningRuleFormValue {
  const firstActiveCurrency = currencies.find(currency => !currency.deletedAt);
  const reward: ObtainRewardAction = rule?.actions?.find(
    ({ kind }) => kind === ActionTypes.REWARD_ATTRIBUTION,
  ) as ObtainRewardAction;
  const trans: TransactionAction = rule?.actions?.find(
    ({ kind }) => kind === ActionTypes.TRANSACTION,
  ) as TransactionAction;
  const activeRewards = rewards?.filter(({ deletedAt }) => !deletedAt) ?? [];

  return {
    name: rule?.name || '',
    criteria: (rule?.criteria || { and: [] }) as Record<string, any>,
    i18nAttributes:
      rule?.i18nAttributes ||
      Object.fromEntries(languages.map(language => [language, { description: '' }])),
    actions: {
      reward: reward?.impact || { rewardId: activeRewards[0]?.id || '' },
      trans: trans?.impact
        ? {
            ...trans.impact,
            value:
              typeof (trans?.impact as TransactionFixedImpact)?.value === 'number'
                ? Math.abs((trans.impact as TransactionFixedImpact).value)
                : undefined,
          }
        : {
            currency: trans?.impact.currency || firstActiveCurrency?.id || '',
            value: undefined,
          },
    },
    options: rule?.options?.periodicityLimitation
      ? rule.options
      : {
          periodicityLimitation: {
            enabled: false,
            periodicity: PeriodicityLimitationEnum.FOREVER,
            occurrence: 1,
            timezone,
          },
        },
    entity: rule,
  };
}

export function formValueToAttributes(formValue: BurningRuleFormValue): RuleAttributes {
  return {
    ...(formValue.entity as RuleAttributes),
    eventSource: EventSource.QUALIFIO_LOYALTY,
    eventName: EventName.BURNING,
    name: formValue.name,
    i18nAttributes: formValue.i18nAttributes,
    type: RuleType.BURN,
    criteria:
      formValue.criteria && !isEqual(formValue.criteria, { and: [] }) ? formValue.criteria : null,
    actions: [
      {
        kind: ActionTypes.TRANSACTION,
        impact: {
          ...formValue.actions.trans,
          value:
            typeof formValue.actions.trans.value === 'number'
              ? Math.abs(formValue.actions.trans.value) * -1
              : 0,
          mode: ImpactMode.FIXED_AMOUNT,
        },
      },
      { kind: ActionTypes.REWARD_ATTRIBUTION, impact: formValue.actions.reward },
    ],
    options: formValue.options,
  };
}
